import {Fragment, type ReactNode} from "react";

// Order matters — URL pattern must come first so `mailto:`/`tel:` URLs in raw form don't
// get pre-eaten by the bare email pattern. Multiline `g` so we walk the whole string.
const URL_REGEX = /\bhttps?:\/\/[^\s]+/g;
const EMAIL_REGEX = /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/g;
// French phone format: +33 or leading 0, then 9 digits in 4 pairs with optional
// separators. Anchored on a word boundary so order numbers and IBAN fragments don't get
// mis-detected.
const PHONE_REGEX = /(?<![\w+])(?:\+33\s?|0)[1-9](?:[\s.-]?\d{2}){4}\b/g;

type Token = {start: number; end: number; href: string; text: string};

/**
 * Render text with URLs, emails and phone numbers turned into clickable anchors. Markdown
 * is intentionally NOT supported (spec: "auto-link sans markdown").
 */
export function autoLinkText(text: string): ReactNode {
  const tokens = collectTokens(text).sort((a, b) => a.start - b.start);
  const merged: Token[] = [];
  let cursor = -1;
  for (const token of tokens) {
    if (token.start >= cursor) {
      merged.push(token);
      cursor = token.end;
    }
  }

  if (merged.length === 0) {
    return text;
  }

  const nodes: ReactNode[] = [];
  let lastIndex = 0;
  merged.forEach((token, i) => {
    if (token.start > lastIndex) {
      nodes.push(<Fragment key={`t-${i}`}>{text.slice(lastIndex, token.start)}</Fragment>);
    }
    nodes.push(
      <a
        key={`a-${i}`}
        href={token.href}
        target={token.href.startsWith("http") ? "_blank" : undefined}
        rel={token.href.startsWith("http") ? "noopener noreferrer" : undefined}
      >
        {token.text}
      </a>
    );
    lastIndex = token.end;
  });
  if (lastIndex < text.length) {
    nodes.push(<Fragment key="tail">{text.slice(lastIndex)}</Fragment>);
  }
  return nodes;
}

function collectTokens(text: string): Token[] {
  const tokens: Token[] = [];
  for (const match of text.matchAll(URL_REGEX)) {
    pushToken(tokens, match, (m) => m[0]);
  }
  for (const match of text.matchAll(EMAIL_REGEX)) {
    pushToken(tokens, match, (m) => `mailto:${m[0]}`);
  }
  for (const match of text.matchAll(PHONE_REGEX)) {
    pushToken(tokens, match, (m) => `tel:${m[0].replace(/[\s.\-]/g, "")}`);
  }
  return tokens;
}

function pushToken(
  tokens: Token[],
  match: RegExpMatchArray,
  hrefBuilder: (m: RegExpMatchArray) => string
) {
  const start = match.index;
  if (start === undefined) {
    return;
  }
  tokens.push({
    start,
    end: start + match[0].length,
    href: hrefBuilder(match),
    text: match[0],
  });
}
